import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _get from 'lodash/get';
import querystring from 'querystring';
import { withRouter } from 'react-router-dom';

import { Box } from '@biome-design-system/ui/Box';
import { Alert } from '@biome-design-system/ui/Alert';
import MapPinSlideSheet from './components/MapPinSlideSheet';
import SearchSummary from './components/SearchSummary';
import { injectIntl, FormattedMessage } from '@kyruus/intl';

// Map drawer components
import { ResultsHeader } from 'Src/shared/ResultsHeader';
import MapCard from '../map-search-v9/components/MapCard/MapCard';
import { FilterBar } from './filter-bar';
import ErrorMessage from '../error/error';
import { AvailabilityControls } from '../shared/AvailabilityControls';
import LoadingOverlay from '../shared/loading';
import ProviderListV9, { FROM_SEARCH_LIST } from '../shared/ProviderListV9';
import { events, pages, placements } from '../tracking/constants';
import { getLogWithProvider } from '../tracking/decorators';
import { getVisibility } from '../utils/search-common';
import NoResultsContent from './no-results';
import { EmergencyDisclaimer } from './EmergencyDisclaimer';
import { SearchFooter } from './SearchFooter';
import SearchMetas from './search-metas';
import { MapSectionContainer, SearchResultsWithMapContainer } from './styles';
import { PatientSearchRefiner } from './PatientSearchRefiner';

import {
  isModuleEnabled,
  isProviderListMapsEnabled,
  MODULES,
  shouldRenderDirectBookInDrawer,
  getConfigProperty
} from 'Common/config';
import { mapSearchCallbackFunction } from '../map-search/lib/utils';
import {
  getProviderDisplayName,
  getProviderPhoneNumber
} from '../provider/utils';
import { V9SearchAlerts } from 'Src/shared/search-alerts/search-alerts';
import { scrollToTopCCF } from '../utils/cerner';
import {
  V9_HAS_AVAILABILITY_PARAM_KEY,
  V9_PATIENT_REL_FILTER_KEY,
  V9_PURPOSE_FILTER_KEY
} from '../utils/constants';
import { getMapSearchURL, mapSearchModuleEnabled } from '../utils/map-search';
import AvailabilityModal from './availability-modal';
import { DISTANCE_ANY } from 'Src/search-v9/Filters/LocationFilter';
import {
  AvailabilityControlsContainer,
  ExpandedFilterDrawerOverlay,
  ProviderListContainer
} from './styles';
import {
  getParamsForConfirmAvailability,
  getPurposeOptionsByPatientRel,
  hidePatientRel,
  showSearchFooterDisclaimer,
  splitObjectKeys
} from './utils';

import { getApptPurposeFilterProps } from 'Src/shared/appt-purpose-filtering';
import { SideDrawerFilterPanel } from 'Src/shared/SideDrawerFilterPanel';
import ProviderMapContainer from './components/ProviderMapContainer';
import { ValueAddPrograms } from './ValueAddPrograms';
import { messages } from './messages';

class Search extends React.Component {
  constructor() {
    super();
    this.state = {
      toggle: false,
      isAvailabilityModalOpen: false,
      availableAppt: null,
      patientRel: '',
      purpose: null,
      purposeHasNoAvailability: null,
      patientRelHasNoAvailability: null,
      shouldHidePatientRel: false,
      showFilterDrawer: false,
      showAvailabilityDrawer: false,
      isPatientSearchRefinerOpen: false,
      hoveredProviderId: null,
      // Map pin drawer state
      isMapPinDrawerOpen: false,
      selectedMapLocation: null,
      providersAtLocation: []
    };

    this.handleOnChangeSwitch = this.handleOnChangeSwitch.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleConfirmModal = this.handleConfirmModal.bind(this);
    this.onPatientRelSelect = this.onPatientRelSelect.bind(this);
    this.onSelectPurpose = this.onSelectPurpose.bind(this);
    this.onClickShowAvailability = this.onClickShowAvailability.bind(this);
    this.onAvailabilityTilesLoaded = this.onAvailabilityTilesLoaded.bind(this);
    this.handleShowFilterDrawer = this.handleShowFilterDrawer.bind(this);
    this.handleHideFilterDrawer = this.handleHideFilterDrawer.bind(this);
    this.handleCloseRefinerDialog = this.handleCloseRefinerDialog.bind(this);
    this.handleProviderHover = this.handleProviderHover.bind(this);
    // Bind map marker click handlers
    this.handleMapMarkerClick = this.handleMapMarkerClick.bind(this);
    this.handleCloseMapPinDrawer = this.handleCloseMapPinDrawer.bind(this);
  }

  componentDidMount() {
    const {
      location,
      setPatientRel,
      setPurpose,
      fetchGlobalPurposesByPatientRelV9,
      log,
      slotsByProviderId,
      fetchSlots,
      history,
      patientSearchRefiner
    } = this.props;

    if (slotsByProviderId) {
      // refresh slots
      Object.entries(slotsByProviderId).forEach(([key, { apptInfo }]) => {
        fetchSlots(apptInfo, [key]);
      });
    }

    const query = querystring.parse(location.search.substring(1));

    // Handle both single filter (string) and multiple filters (array)
    let querystringFilters = null;
    if (query.filter) {
      if (Array.isArray(query.filter) && query.filter.length) {
        // Multiple filters case
        querystringFilters = Object.assign(...splitObjectKeys(query.filter));
      } else if (typeof query.filter === 'string') {
        // Single filter case
        querystringFilters = Object.assign(...splitObjectKeys([query.filter]));
      }
    }

    const querystringPurpose =
      querystringFilters && querystringFilters[V9_PURPOSE_FILTER_KEY];

    if (querystringPurpose) {
      const querystringPatientRel =
        querystringFilters[V9_PATIENT_REL_FILTER_KEY];

      this.setState({
        purpose: {
          name: querystringPurpose
        },
        patientRel: querystringPatientRel,
        availableAppt: {
          patientRel: querystringPatientRel,
          purpose: querystringPurpose
        },
        toggle: true
      });

      setPatientRel(querystringPatientRel);
      setPurpose(querystringPurpose);
      querystringPatientRel &&
        fetchGlobalPurposesByPatientRelV9(querystringPatientRel, log);
    }

    // Check for standalone patient relationship filter (without purpose)
    // This handles cases like direct URL entry, page refresh, or back navigation
    if (!querystringPurpose && querystringFilters) {
      const standalonePatientRel =
        querystringFilters[V9_PATIENT_REL_FILTER_KEY];

      if (
        standalonePatientRel &&
        (standalonePatientRel === 'new' ||
          standalonePatientRel === 'established')
      ) {
        setPatientRel(standalonePatientRel);
        log('user_action.patient_type_filter_applied', {
          patient_relationship: standalonePatientRel
        });
      }
    }

    if (patientSearchRefiner) {
      this.setState({ isPatientSearchRefinerOpen: true });
    }

    if (query.relationship && query.purpose) {
      // remove those params to avoid cross-contamination with other providers
      // after returning to search from a provider profile with availability params attached
      const copyOfQuery = {
        ...query
      };

      delete copyOfQuery.relationship;
      delete copyOfQuery.purpose;

      history.push(`/search?${querystring.stringify(copyOfQuery)}`);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      searchSummary,
      location,
      searchContext,
      apptInfo,
      log,
      fetchGlobalPurposesByPatientRelV9,
      patientSearchRefiner,
      setPatientRel
    } = this.props;

    const { isPatientSearchRefinerOpen } = this.state;

    const query = querystring.parse(location.search.substring(1));
    const prevQuery = querystring.parse(prevProps.location.search.substring(1));

    const locationChanged = prevProps.location.search !== location.search;
    const searchTermChanged =
      prevProps?.searchSummary?.search_query &&
      prevProps.searchSummary.search_query !== searchSummary.search_query;

    // Sync Patient Type filter with Redux availability state
    if (locationChanged) {
      const currentFilters = Array.isArray(query.filter)
        ? query.filter
        : [query.filter].filter(Boolean);
      const prevFilters = Array.isArray(prevQuery.filter)
        ? prevQuery.filter
        : [prevQuery.filter].filter(Boolean);

      // Extract patient relationship from current filters
      const currentPatientRelFilter = currentFilters.find(
        (f) =>
          f &&
          f.includes('provider.appointment_ehr_purposes.patient_relationship:')
      );

      // Extract patient relationship from previous filters
      const prevPatientRelFilter = prevFilters.find(
        (f) =>
          f &&
          f.includes('provider.appointment_ehr_purposes.patient_relationship:')
      );

      // Check if patient relationship filter changed
      if (currentPatientRelFilter !== prevPatientRelFilter) {
        if (currentPatientRelFilter) {
          // Extract the value (new, established)
          const relationshipValue = currentPatientRelFilter.split(':')[1];

          if (
            relationshipValue === 'new' ||
            relationshipValue === 'established'
          ) {
            if (typeof setPatientRel === 'function') {
              setPatientRel(relationshipValue);

              log('user_action.patient_type_filter_applied', {
                patient_relationship: relationshipValue
              });
            }
          }
        } else {
          // Filter was removed, clear the patient relationship
          if (typeof setPatientRel === 'function') {
            setPatientRel('');
          }
        }
      }
    }

    // reset state if the search term or the url changes
    if (searchTermChanged || locationChanged) {
      let newState = { providerId: null };

      const filtersReset = locationChanged && query.filter == null;
      const psrSkipped =
        !prevQuery.psr_refined &&
        query.psr_refined != null &&
        query.psr_refined;
      // reset everything if the search term changed, but not if the patient search refiner was skipped
      if (!psrSkipped && (searchTermChanged || filtersReset)) {
        this.props.resetAvailability();
        newState = {
          ...newState,
          availableAppt: null,
          patientRel: '',
          purpose: null,
          toggle: false
        };
      }

      this.setState(newState);
    }

    const newRefinerExists =
      prevProps.patientSearchRefiner !== patientSearchRefiner;
    const openPreviousRefiner = query.patient_search_refiner === 'true';

    if (
      patientSearchRefiner &&
      !isPatientSearchRefinerOpen &&
      (newRefinerExists || openPreviousRefiner)
    ) {
      this.setState({ isPatientSearchRefinerOpen: true });
    } else if (!patientSearchRefiner && isPatientSearchRefinerOpen) {
      this.setState({ isPatientSearchRefinerOpen: false });
    }

    if (searchContext !== prevProps.searchContext && apptInfo.relationship) {
      fetchGlobalPurposesByPatientRelV9(apptInfo.relationship, log);
    }
  }

  reset() {
    const { patientRel, purpose } = this.state;
    const { history, getUpdatedSearch, config } = this.props;
    const { v9DirectBookEnabledFilter } = config.visibility;

    this.props.resetAvailability();
    this.setState({
      availableAppt: null,
      patientRel: '',
      purpose: null
    });

    const filter = [
      `${V9_PATIENT_REL_FILTER_KEY}:${patientRel}`,
      `${V9_PURPOSE_FILTER_KEY}:${purpose.name}`,
      v9DirectBookEnabledFilter
    ];

    history.push(
      getUpdatedSearch([
        { action: 'delete_key_value', key: 'filter', value: filter },
        {
          action: 'delete_key',
          key: V9_HAS_AVAILABILITY_PARAM_KEY,
          value: true
        }
      ])
    );
  }

  handleCloseRefinerDialog() {
    this.setState({ isPatientSearchRefinerOpen: false });
  }

  // Handle provider hover, keyboard focus, and marker keyboard activation for map highlighting
  handleProviderHover(providerId) {
    this.setState({ hoveredProviderId: providerId });
  }

  handleOnChangeSwitch() {
    const { log } = this.props;
    this.setState((prev) => ({ toggle: !prev.toggle }));
    if (this.state.providerId) {
      this.setState(
        {
          isAvailabilityModalOpen: true,
          patientRel: '',
          providerId: null
        },
        () => {
          this.onPatientRelSelect('new');
        }
      );
    }
    if (this.state.availableAppt) {
      log('user_action.show.provider_availability_toggle_off');
      this.reset();
    } else {
      log('user_action.show.provider_availability_toggle_on');
      this.setState({
        isAvailabilityModalOpen: true,
        patientRel: 'new',
        providerId: null
      });
      this.onPatientRelSelect('new');
    }

    // never hide patient rel for global modal
    this.setState({
      shouldHidePatientRel: false
    });

    scrollToTopCCF();
  }

  handleEditClick() {
    const { log } = this.props;
    log('user_action.show.provider_availability_toggle_edit');
    this.setState({
      isAvailabilityModalOpen: true
    });
    scrollToTopCCF();
  }

  handleCloseModal(type) {
    const { log } = this.props;
    log(`user_action.show_available_appt_modal.${type}`);
    const { availableAppt } = this.state;
    const isAvailableAppt = !!availableAppt;

    let patientRel, purpose;

    if (isAvailableAppt) {
      patientRel = availableAppt.patientRel;
      purpose = { name: availableAppt.purpose };
    } else {
      patientRel = '';
      purpose = null;
    }

    this.setState({
      isAvailabilityModalOpen: false,
      patientRel,
      purpose,
      toggle: isAvailableAppt,
      providerId: null,
      purposeHasNoAvailability: null,
      patientRelHasNoAvailability: null
    });
  }

  handleConfirmModal() {
    const { purpose, patientRel, providerId } = this.state;
    const {
      setPurpose,
      setPatientRel,
      getUpdatedSearch,
      history,
      fetchSlots,
      config
    } = this.props;

    const availableAppt = providerId
      ? null
      : {
          patientRel: patientRel,
          purpose: purpose.name
        };

    if (availableAppt) {
      this.setState({
        isAvailabilityModalOpen: false,
        availableAppt,
        providerId: null,
        purpose: providerId ? null : purpose,
        patientRel: providerId ? null : patientRel
      });
      const params = getParamsForConfirmAvailability({
        config,
        purpose,
        patientRel
      });
      history.push(getUpdatedSearch(params));
    } else {
      this.setState({
        isAvailabilityModalOpen: false,
        providerId
      });
    }
    setPatientRel(patientRel);
    setPurpose(purpose.name);
    if (providerId) {
      fetchSlots(
        {
          relationship: patientRel,
          purpose: purpose.name
        },
        [providerId]
      );
    }
  }

  onPatientRelSelect(patientRel) {
    const { apptInfo, fetchGlobalPurposesByPatientRelV9, log } = this.props;
    const { providerId, availableAppt } = this.state;
    log('user_action.provider_availability_type_selected', {
      patient_rel: patientRel
    });
    this.setState(
      (state) => {
        const newStateFragment = {
          patientRel,
          purpose: null,
          purposeHasNoAvailability: false
        };

        if (
          state.providerId != null &&
          this.props.availablePurposesByProviderId != null &&
          this.props.availablePurposesByProviderId[this.state.providerId] !=
            null
        ) {
          const availablePurposes =
            this.props.availablePurposesByProviderId[this.state.providerId];

          if (availablePurposes) {
            newStateFragment.patientRelHasNoAvailability =
              !availablePurposes[patientRel].size;
          }
        }
        return newStateFragment;
      },
      () =>
        (!providerId || availableAppt) &&
        !apptInfo.purposeOptions[patientRel].length &&
        fetchGlobalPurposesByPatientRelV9(patientRel, log)
    );
  }

  onSelectPurpose(purpose) {
    this.props.log('user_action.appt_purpose_type_selected', {
      appt_purpose: purpose
    });

    if (this.state.providerId) {
      const availablePurposes =
        this.props.availablePurposesByProviderId[this.state.providerId];

      this.setState({
        purpose,
        purposeHasNoAvailability: !availablePurposes[this.state.patientRel].has(
          purpose.name
        )
      });
    }

    this.setState({ purpose });
  }

  onClickShowAvailability(provider) {
    const { config, log, fetchPurposesByProviderId } = this.props;
    log('user_action.show.provider_availability_tile', {
      provider_id: provider.id
    });
    const shouldHidePatientRel = hidePatientRel(
      provider,
      config.direct_book.hide_patient_rel_for_locations
    );
    const defaultPatientRel = shouldHidePatientRel ? 'established' : '';
    if (this.state.providerId) {
      this.setState({
        isAvailabilityModalOpen: true,
        providerId: provider.id,
        patientRel: defaultPatientRel,
        purpose: null,
        shouldHidePatientRel: shouldHidePatientRel
      });
    } else {
      this.setState({
        isAvailabilityModalOpen: true,
        providerId: provider.id,
        patientRel: defaultPatientRel,
        shouldHidePatientRel: shouldHidePatientRel
      });
    }

    fetchPurposesByProviderId(provider.id, log);
    scrollToTopCCF();
  }

  shouldOpenDirectBookSameWindow() {
    const { config } = this.props;
    return isModuleEnabled(config, MODULES.DIRECT_BOOK);
  }

  onAvailabilityTilesLoaded(availabilitiesCount, provider_id) {
    const { providerId, log, config } = this.props;
    const isHeadsUpAvailabilityEnabled = _get(
      config,
      'darkship_feature_flags.provider_tile_heads_up_availability',
      false
    );
    if (this.state.providerId === providerId && !isHeadsUpAvailabilityEnabled) {
      log('user_action.show_show_available_appt_times', {
        provider_id,
        count: availabilitiesCount.length
      });
    }
  }

  handleShowFilterDrawer() {
    this.setState({ showFilterDrawer: true });
  }

  handleHideFilterDrawer() {
    this.setState({ showFilterDrawer: false });
  }

  handleShowAvailabilityDrawer = (providerId) => {
    const { log } = this.props;
    log('user_action.open_availability_drawer', {
      providerId
    });

    this.setState({ showAvailabilityDrawer: true });
  };

  handleHideAvailabilityDrawer = (providerId, reason) => {
    const { log } = this.props;
    log('user_action.close_availability_drawer', {
      providerId,
      close_reason: reason
    });
    this.setState({ showAvailabilityDrawer: false });
  };

  /**
   * Handle marker click event from ProviderMapContainer
   * Opens a drawer with location and provider information
   * @param {Object} data - Object containing location data and map instance
   */
  handleMapMarkerClick({ location, locationId }) {
    const { providers, log } = this.props;

    // Find providers at this location
    const providersAtLocation = this.getProvidersForLocation(
      providers,
      locationId
    );

    // Set state to open drawer
    this.setState({
      isMapPinDrawerOpen: true,
      selectedMapLocation: location,
      providersAtLocation
    });

    // Log analytics event
    log('provider_map_pin_drawer_open', {
      locationId,
      locationName: location.name || 'Unknown',
      providerCount: providersAtLocation.length
    });
  }

  /**
   * Close the map pin drawer
   * @param {Event} event - The event that triggered the close
   * @param {string} reason - The reason for closing ('escapeKeyDown', 'backdropClick', 'closeClick')
   */
  handleCloseMapPinDrawer(event, reason) {
    const { log } = this.props;
    const { selectedMapLocation } = this.state;

    // Log analytics event
    if (selectedMapLocation) {
      log('provider_map_pin_drawer_close', {
        locationId: selectedMapLocation.id,
        closeReason: reason || 'user_action'
      });
    }

    // Reset state
    this.setState({
      isMapPinDrawerOpen: false,
      selectedMapLocation: null,
      providersAtLocation: []
    });
  }

  /**
   * Find all providers that have a location with the given locationId
   * @param {Array} providersList - Array of provider objects
   * @param {string} locationId - ID of the location to filter by
   * @returns {Array} - Filtered list of providers at the specified location
   */
  getProvidersForLocation(providersList, locationId) {
    if (!providersList || !locationId) return [];

    // Filter providers that have this location
    return providersList.filter(
      (provider) =>
        provider &&
        provider.locations &&
        provider.locations.some((locationNew) => locationNew?.id === locationId)
    );
  }

  getPhoneNumberForSelectedProvider(selectedProviderId, providers) {
    if (selectedProviderId) {
      const selectedProvider = providers.find(
        (provider) => selectedProviderId === provider.id
      );

      if (selectedProvider) {
        return getProviderPhoneNumber(selectedProvider);
      }
    }

    return null;
  }

  getSelectedProvider() {
    return this.props.providers.find((provider) => {
      return this.state.providerId === provider.id;
    });
  }

  getSlotsVisibility() {
    const { schedulingVisibility } = getVisibility(this.props.config);
    return schedulingVisibility;
  }

  render() {
    const {
      searchSummary,
      config,
      facets,
      getUpdatedSearch,
      providers,
      totalProviders,
      totalPages,
      suggestions,
      topSpecialties,
      log,
      loading,
      slotsCountLoading,
      error,
      showMobileFacets,
      distanceSort,
      sortOptions,
      apptInfo,
      availabilityButtonsConfig,
      searchProviderIds,
      slotsByProviderId,
      slotsStatusByProviderId = {},
      location,
      history,
      customerCode,
      productName,
      intl,
      alerts,
      match,
      fetchProviderAvailability,
      fetchPurposesByProviderId,
      purposeOptionsByProviderId,
      availabilityError,
      purposesLoading,
      patientSearchRefiner,
      valueAddPrograms
    } = this.props;

    const showAvailabilityControls = getConfigProperty(
      config,
      'feature_flags.global_availability_controls'
    );

    const showLoadingOverlay = loading || slotsCountLoading;

    const handleTimeSlotsClick = (_, { date, time, providerId, href }) => {
      const provider = providers.find((p) => p.id === providerId);

      getLogWithProvider(log, provider)(
        'user_action.provider_timeslot_selected_search',
        {
          date,
          time
        }
      );
      if (shouldRenderDirectBookInDrawer(config) && provider) {
        history.push(href, {
          provider: {
            location,
            provider
          }
        });
      }
    };

    const handleViewMoreClick = (_, { providerId, viewMoreBookOnlineUrl }) => {
      const provider = providers.find((p) => p.id === providerId);

      getLogWithProvider(
        log,
        provider
      )(
        `${events.USER_ACTION}.${pages.SEARCH_RESULTS}.${placements.PROVIDER_CARD}.availability_tiles.view_all_appointments`
      );
      if (shouldRenderDirectBookInDrawer(config) && provider) {
        if (this.state.isMapPinDrawerOpen) {
          this.handleCloseMapPinDrawer(null, 'view_all_appointments_click');
        }
        history.push(viewMoreBookOnlineUrl, {
          provider: {
            location,
            provider
          }
        });
      }
    };

    const shouldDisplayAvailability = config.display_availability_in_search;

    if (error) {
      return (
        <div>
          <LoadingOverlay loading={showLoadingOverlay} />
          <ErrorMessage />
        </div>
      );
    }

    let providerContent;

    const {
      availableAppt,
      patientRel,
      isAvailabilityModalOpen,
      purpose,
      shouldHidePatientRel,
      // Get hover state
      hoveredProviderId,
      // Map pin drawer state
      isMapPinDrawerOpen,
      selectedMapLocation,
      providersAtLocation
    } = this.state;

    const bookUrlTarget = this.shouldOpenDirectBookSameWindow()
      ? '_self'
      : undefined;

    if (totalProviders == 0) {
      if (!_isEmpty(suggestions) && !loading) {
        log('user_action.search_results.spell_suggestion_available', {
          suggestions: _map(suggestions, 'display_text').toString(),
          search_query: searchSummary.search_query
        });
      }

      providerContent = _get(
        config,
        'darkship_feature_flags.map_show_search_this_area_button',
        false
      ) ? (
        <Alert variant="outlined" severity="warning">
          <FormattedMessage {...messages.noResultsBanner} />
        </Alert>
      ) : (
        <NoResultsContent
          suggestions={suggestions}
          topSpecialties={topSpecialties}
          getUpdatedSearch={getUpdatedSearch}
          searchSummary={searchSummary}
          log={log}
          history={history}
          config={config}
        />
      );
    } else {
      providerContent = (
        <ProviderListV9
          providers={providers}
          customerCode={customerCode}
          productName={productName}
          config={config}
          searchSummary={searchSummary}
          log={log}
          slots={slotsByProviderId}
          slotsStatus={slotsStatusByProviderId}
          onClickShowAvailability={this.onClickShowAvailability}
          availabilityLinkTarget={bookUrlTarget}
          onTimeSlotsClick={handleTimeSlotsClick}
          onViewMoreClick={handleViewMoreClick}
          onAvailabilityTilesLoaded={this.onAvailabilityTilesLoaded}
          profileSummaryParams={{
            from: FROM_SEARCH_LIST
          }}
          appointmentInfo={apptInfo}
          fetchProviderAvailability={fetchProviderAvailability}
          fetchPurposesByProviderId={fetchPurposesByProviderId}
          purposeOptionsByProviderId={purposeOptionsByProviderId}
          availabilityError={availabilityError}
          purposesLoading={purposesLoading}
          onProviderHover={this.handleProviderHover}
          onKeyboardFocus={this.handleProviderHover}
          onKeyboardBlur={() => this.handleProviderHover(null)}
          isProviderListMapsEnabled={isProviderListMapsEnabled(config)}
        />
      );
    }

    const showExpandedSearchRadiusBanner = (searchSummary) => {
      const { distance, _original_input_distance } = searchSummary;
      if (
        typeof distance === 'undefined' ||
        typeof _original_input_distance === 'undefined'
      ) {
        return false;
      }
      return distance != _original_input_distance;
    };

    const isFullyExpandedSearchRadius = searchSummary.distance === DISTANCE_ANY;

    const showAvailabilityDisclaimer = showSearchFooterDisclaimer(providers);

    const bookOnlineUrlExists = (providers || []).some((provider) =>
      Boolean(provider.book_online_url)
    );

    const isGlobalApptInfoSelected =
      apptInfo?.relationship && apptInfo?.purpose;

    const showGlobalAvailabilityControls =
      showAvailabilityControls &&
      (bookOnlineUrlExists ||
        // prevent dead end when global purpose is selected and no providers are returned
        (isGlobalApptInfoSelected && !searchProviderIds.length));

    const isSpecialtyPage = location.pathname.includes('/specialty');
    const isSpecialtyLocationPage =
      isSpecialtyPage && location.pathname.includes('/near');
    const additionalParams = (() => {
      if (isSpecialtyLocationPage) {
        return {
          specialties: searchSummary.specialties,
          location: searchSummary.location,
          distance: searchSummary.distance
        };
      }

      if (isSpecialtyPage) {
        return {
          specialties: searchSummary.specialties
        };
      }

      return {};
    })();
    const mapSearchUrl = getMapSearchURL(
      searchSummary.query_string,
      additionalParams
    );

    const primaryMlocFacet =
      facets &&
      facets.find(
        (f) => f.field === 'locations.primary_marketable_location_id'
      );

    const shouldShowMapLink =
      totalProviders > 0 &&
      mapSearchModuleEnabled(config) &&
      primaryMlocFacet &&
      primaryMlocFacet.total > 0;
    return (
      <main className="search-container mt-m">
        {patientSearchRefiner && this.state.isPatientSearchRefinerOpen && (
          <PatientSearchRefiner
            fullScreen={getConfigProperty(
              config,
              'patient_search_refiner.full_screen'
            )}
            getUpdatedSearch={getUpdatedSearch}
            log={log}
            onClose={this.handleCloseRefinerDialog}
            patientSearchRefiner={patientSearchRefiner}
          />
        )}
        {this.state.showFilterDrawer && (
          <ExpandedFilterDrawerOverlay onClick={this.handleHideFilterDrawer} />
        )}
        <Box component="section" aria-busy={loading ? 'true' : 'false'}>
          <FilterBar
            facets={facets}
            getUpdatedSearch={getUpdatedSearch}
            searchSummary={searchSummary}
            config={config}
            allFiltersButton={{
              onClick: this.handleShowFilterDrawer,
              ariaExpanded: this.state.showFilterDrawer,
              ariaControls: 'search-filter-drawer',
              ariaHasPopup: 'dialog'
            }}
            mapSearchUrl={mapSearchUrl}
            log={log}
            shouldShowMapLink={shouldShowMapLink}
            onMapButtonClick={(e) => {
              mapSearchCallbackFunction(e, log, mapSearchUrl, history);
            }}
            disabled={totalProviders === 0}
          />
        </Box>
        <div>
          <SearchMetas
            config={config}
            match={match}
            searchSummary={searchSummary}
          />
          <EmergencyDisclaimer
            config={config}
            closeButtonAriaLabel={intl.formatMessage(
              messages.alertCloseButtonAriaLabel
            )}
            log={log}
          />
          <LoadingOverlay
            loading={showLoadingOverlay}
            filterDrawerOpen={this.state.showFilterDrawer}
          />
        </div>
        <Box component="section" mt={2}>
          {/* Temporary check to show/hide filters inside drawer */}
          {this.state.showFilterDrawer && (
            <SideDrawerFilterPanel
              config={config}
              distanceSort={distanceSort}
              facets={facets}
              getUpdatedSearch={getUpdatedSearch}
              loading={showLoadingOverlay}
              log={log}
              onClose={this.handleHideFilterDrawer}
              searchSummary={searchSummary}
              showLocationFacet={true}
              showMobileFacets={showMobileFacets}
            />
          )}
          <Box component="section" aria-busy={loading ? 'true' : 'false'}>
            {shouldDisplayAvailability &&
              !this.props.hasFetchingSlotCountsFailed &&
              (!loading || searchSummary.search_query) &&
              showGlobalAvailabilityControls && (
                <AvailabilityControlsContainer>
                  <AvailabilityControls
                    handleEdit={this.handleEditClick}
                    handleToggle={this.handleOnChangeSwitch}
                    selectedAppointment={availableAppt}
                  />
                </AvailabilityControlsContainer>
              )}
            <AvailabilityModal
              patientRel={
                (availableAppt && !patientRel && availableAppt.patientRel) ||
                patientRel
              }
              purpose={
                (availableAppt &&
                  !patientRel &&
                  !purpose && { name: availableAppt.purpose }) ||
                purpose
              }
              isOpen={isAvailabilityModalOpen}
              onPatientRelSelect={this.onPatientRelSelect}
              onSelectPurpose={this.onSelectPurpose}
              handleCloseModal={this.handleCloseModal}
              handleConfirmModal={this.handleConfirmModal}
              patientRelOptions={
                this.state.providerId
                  ? getPurposeOptionsByPatientRel(
                      this.getSelectedProvider(),
                      this.getSlotsVisibility()
                    )
                  : apptInfo.purposeOptions
              }
              providerDisplayName={
                this.state.providerId &&
                getProviderDisplayName(this.getSelectedProvider())
              }
              providerPhone={this.getPhoneNumberForSelectedProvider(
                this.state.providerId,
                providers
              )}
              availabilityButtonsConfig={availabilityButtonsConfig}
              isPurposeLoading={apptInfo.loading}
              log={log}
              patientRelHasNoAvailability={
                this.state.patientRelHasNoAvailability
              }
              purposeHasNoAvailability={this.state.purposeHasNoAvailability}
              hidePatientRel={shouldHidePatientRel}
              apptPurposeFilterProps={getApptPurposeFilterProps({
                intl,
                config
              })}
            />
            <h1 className="sr-only">
              <FormattedMessage {...messages.providerListHeader} />
            </h1>
            <V9SearchAlerts config={config} searchAlerts={alerts} />
            {Number.isInteger(totalProviders) && (
              <ResultsHeader
                config={config}
                getUpdatedSearch={getUpdatedSearch}
                isLoadingOverlayShown={showLoadingOverlay}
                log={log}
                searchSummary={searchSummary}
                sortOptions={sortOptions}
                sx={{ mb: 2 }}
                totalProviders={totalProviders}
              />
            )}
            {showExpandedSearchRadiusBanner(searchSummary) && !loading && (
              <Alert
                data-testid="ExpandedSearchRadiusBanner"
                severity="warning"
                variant="outlined"
                sx={(theme) => ({
                  marginTop: theme.spacing(2.5),
                  marginBottom: theme.spacing(2.5)
                })}
              >
                <FormattedMessage
                  {...(isFullyExpandedSearchRadius &&
                  searchSummary.search_description != null
                    ? messages.providerListExpandedSearchRadiusFullWarning
                    : messages.providerListExpandedSearchRadiusPartialWarning)}
                  values={{
                    expandedDistance: searchSummary.distance,
                    originalDistance: searchSummary._original_input_distance,
                    searchDescription: searchSummary.search_description
                  }}
                >
                  {(text) => <span role="status">{text}</span>}
                </FormattedMessage>
              </Alert>
            )}
            <ValueAddPrograms valueAddPrograms={valueAddPrograms} log={log} />

            {/* Always show search results, drawer will appear over them */}
            {isProviderListMapsEnabled(config) ? (
              <SearchResultsWithMapContainer>
                <ProviderListContainer>{providerContent}</ProviderListContainer>
                <MapSectionContainer>
                  <ProviderMapContainer
                    providers={providers}
                    config={config}
                    appSettings={this.props.appSettings}
                    hoveredProviderId={hoveredProviderId}
                    log={this.props.log}
                    onMarkerClick={this.handleMapMarkerClick}
                    onKeyboardActivation={this.handleProviderHover}
                    getUpdatedSearch={getUpdatedSearch}
                    location={location}
                    match={match}
                  />
                </MapSectionContainer>
              </SearchResultsWithMapContainer>
            ) : (
              providerContent
            )}

            <SearchFooter
              searchSummary={searchSummary}
              showAvailabilityDisclaimer={showAvailabilityDisclaimer}
              totalPages={totalPages}
              getUpdatedSearch={getUpdatedSearch}
              config={config}
              log={log}
              suggestions={totalProviders > 0 ? suggestions : []}
            />
          </Box>
        </Box>
        {isMapPinDrawerOpen && selectedMapLocation && (
          <MapPinSlideSheet
            dataTestId="map-pin-drawer"
            anchor="left"
            width="70vw"
            id="map-pin-drawer"
            ariaLabelledBy="map-pin-drawer-title"
            isOpen={isMapPinDrawerOpen}
            onClose={(e, reason) => this.handleCloseMapPinDrawer(e, reason)}
          >
            {/* Location Information */}
            <MapCard
              locationData={{
                ...selectedMapLocation,
                address: {
                  city: selectedMapLocation.city || '',
                  state: selectedMapLocation.state || '',
                  zip: selectedMapLocation.zip || '',
                  street1: selectedMapLocation.street1 || '',
                  street2: selectedMapLocation.street2 || ''
                }
              }}
              currentQuery={location.search}
              onMobileCancelHandler={this.handleCloseMapPinDrawer}
              onLocationClick={(url) => {
                if (url) {
                  this.props.log('location_card_click', {
                    location_id: selectedMapLocation.id,
                    location_name: selectedMapLocation.name
                  });
                  history.push(url);
                  this.handleCloseMapPinDrawer();
                }
              }}
            />
            <SearchSummary
              providerCount={providersAtLocation.length}
              searchTerm={searchSummary?.search_query || ''}
              locationName={selectedMapLocation.name}
            />

            {/* Provider List */}
            {providersAtLocation.length > 0 ? (
              <ProviderListV9
                providers={providersAtLocation}
                customerCode={this.props.customerCode}
                productName={this.props.productName}
                config={config}
                searchSummary={searchSummary}
                log={this.props.log}
                shouldAppendAnalyticsParamsBookOnlineUrl={true}
                profileSummaryParams={{
                  location_id: selectedMapLocation.id
                }}
                onClickShowAvailability={this.onClickShowAvailability}
                onTimeSlotsClick={handleTimeSlotsClick}
                onViewMoreClick={handleViewMoreClick}
                slots={this.props.slotsByProviderId}
                slotsStatus={this.props.slotsStatusByProviderId}
                availabilityLinkTarget={bookUrlTarget}
                onAvailabilityTilesLoaded={this.onAvailabilityTilesLoaded}
                appointmentInfo={apptInfo}
                fetchProviderAvailability={fetchProviderAvailability}
                fetchPurposesByProviderId={fetchPurposesByProviderId}
                purposeOptionsByProviderId={purposeOptionsByProviderId}
                availabilityError={availabilityError}
                purposesLoading={purposesLoading}
              />
            ) : (
              <div style={{ padding: '20px' }}>
                <h3>No providers found at this location</h3>
              </div>
            )}
          </MapPinSlideSheet>
        )}
      </main>
    );
  }
}

export default withRouter(injectIntl(Search));
